import { formatPlannedMinutes, resolvePlannedMinutes } from "../../utils/plannedMinutes";
export function asArray(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.users)) return value.users;
  if (Array.isArray(value?.results)) return value.results;
  return [];
}

export function uniqueOptions(items, keyField, labelField) {
  const map = new Map();
  for (const item of items) {
    const key = String(item?.[keyField] || "").trim();
    const label = String(item?.[labelField] || "").trim();
    if (!key || key === "zonder-project" || key === "onbekend") continue;
    if (!map.has(key)) {
      map.set(key, { value: key, label });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "nl-NL"));
}

export function normalizeWerkbon(item, monteurs) {
  const id = String(item?._id || item?.id || "");
  const statusKey = normalizeStatusKey(item?.status);
  const assignedTo = getAssignedToValue(item);
  const assignedUsers = getAssignedUserIds(item);
  const primaryAssignedUserId = assignedTo || assignedUsers[0] || "";
  const assignedLabel = resolveAssignedLabel(primaryAssignedUserId, monteurs);
  const plannedDate = normalizeDate(item?.plannedDate || item?.plannedStartAt);
  const plannedEndDate = normalizeDate(item?.plannedEndAt);
  const durationMinutes = resolveDurationMinutes(item);
  const workTypeLabel = getWorkTypeLabel(item);
  const priorityKey = normalizePriority(item?.priority);
  const scopeId = String(item?.scopeId?._id || item?.scopeId || "").trim();
  const scopeLabel = String(
    item?.scopeId?.name || item?.scopeName || item?.scopeCode || ""
  ).trim();
  const statusPresentation = getStatusPresentation({
    statusKey,
    plannedDate,
    plannedEndDate,
    primaryAssignedUserId,
  });

  return {
    id,
    projectIdRaw: String(
      item?.projectId?._id || item?.projectId || item?.projectNaam || ""
    ).trim(),
    werkbonNummer: item?.werkbonNummer || item?.nummer || "Werkbon",
    title: String(
      item?.omschrijving ||
        item?.titel ||
        item?.opdracht?.omschrijving ||
        item?.scope?.samenvatting ||
        "Werkbon"
    ).trim(),
    clientName: getClientName(item),
    clientKey:
      String(item?.clientId?._id || item?.clientId || getClientName(item))
        .trim()
        .toLowerCase() || "onbekend",
    locationLabel: getLocationLabel(item),
    projectLabel: getProjectLabel(item),
    projectKey: getProjectKey(item),
    workTypeLabel,
    disciplineLabel: workTypeLabel,
    disciplineKey: normalizeText(workTypeLabel) || "algemeen",
    priorityKey,
    priorityLabel: getPriorityLabel(priorityKey),
    priorityTone: getPriorityTone(priorityKey),
    scopeId,
    scopeLabel,
    plannedDate,
    createdAt: item?.createdAt || item?.updatedAt || null,
    startTime:
      String(item?.plannedStartTime || item?.startTime || "").trim() || "08:00",
    durationMinutes,
    durationLabel: formatPlannedMinutes(durationMinutes),
    statusKey,
    statusLabel: statusPresentation.label,
    statusDot: statusPresentation.dotClass,
    statusBadgeClass: statusPresentation.badgeClass,
    assignedUserIds: Array.from(new Set([primaryAssignedUserId, ...assignedUsers].filter(Boolean))),
    primaryAssignedUserId,
    assignedLabel,
    photoCount: Array.isArray(item?.photos)
      ? item.photos.length
      : Array.isArray(item?.fotos)
      ? item.fotos.length
      : 0,
    materialCount: Array.isArray(item?.materialen) ? item.materialen.length : 0,
    hourCount: Array.isArray(item?.hourEntries)
      ? item.hourEntries.length
      : Array.isArray(item?.uren)
      ? item.uren.length
      : 0,
    timelineColor: getTimelineColor(workTypeLabel),
    isBlocked: statusKey === "tijdelijk_stilgelegd",
  };
}

export function normalizeStatusKey(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "aangemaakt" || key === "nieuw") return "concept";
  if (key === "gereed") return "afgerond";
  if (key === "gefactureerd") return "gesloten";
  return key || "concept";
}

export function toISODate(date) {
  const instance = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(instance.getTime())) return "";
  const year = instance.getFullYear();
  const month = `${instance.getMonth() + 1}`.padStart(2, "0");
  const day = `${instance.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseTimeToMinutes(value) {
  const [hours, minutes] = String(value || "08:00").split(":");
  return Number(hours || 8) * 60 + Number(minutes || 0);
}

export function getCapacityTone(ratio) {
  if (ratio >= 1) return "over";
  if (ratio >= 0.8) return "warning";
  return "ok";
}

function getAssignedToValue(item) {
  if (item?.assignedTo && typeof item.assignedTo === "object") {
    return String(item.assignedTo._id || item.assignedTo.userId || "");
  }
  return String(item?.assignedTo || "");
}

function getAssignedUserIds(item) {
  if (!Array.isArray(item?.assignedUsers)) return [];
  return item.assignedUsers
    .map((entry) =>
      typeof entry === "object"
        ? String(entry?._id || entry?.userId || "")
        : String(entry || "")
    )
    .filter(Boolean);
}

function getClientName(item) {
  return (
    String(
      item?.clientId?.naam || item?.clientName || item?.klantNaam || item?.klant || ""
    ).trim() || "Onbekende klant"
  );
}

function getLocationLabel(item) {
  return (
    String(
      item?.locationText || item?.location || item?.opdracht?.locatie || item?.adres || ""
    ).trim() || "Locatie onbekend"
  );
}

function getProjectLabel(item) {
  const overviewProjectName = String(item?.projectNaam || "").trim();
  if (overviewProjectName) return overviewProjectName;
  const nummer = String(item?.projectNummer || item?.projectId?.projectNummer || "").trim();
  const naam = String(item?.projectId?.naam || item?.projectId?.title || "").trim();
  if (nummer && naam) return `${nummer} • ${naam}`;
  return nummer || naam || "Zonder project";
}

function getProjectKey(item) {
  return (
    String(
      item?.projectId?._id ||
        item?.projectId ||
        item?.projectNummer ||
        item?.projectNaam ||
        ""
    )
      .trim()
      .toLowerCase() ||
    "zonder-project"
  );
}

function getWorkTypeLabel(item) {
  const raw = String(item?.workorderType || item?.type || item?.werkType || "").trim().toLowerCase();
  if (raw.includes("stor")) return "Storing";
  if (raw.includes("project")) return "Project";
  return "Service";
}

function normalizePriority(value) {
  const key = String(value || "normaal").trim().toLowerCase();
  if (["spoed", "hoog", "normaal", "laag"].includes(key)) return key;
  return "normaal";
}

function getPriorityLabel(key) {
  if (key === "spoed") return "Spoed";
  if (key === "hoog") return "Hoog";
  if (key === "laag") return "Laag";
  return "Normaal";
}

function getPriorityTone(key) {
  if (key === "spoed") return "bg-rose-100 text-rose-700";
  if (key === "hoog") return "bg-amber-100 text-amber-800";
  if (key === "laag") return "bg-emerald-100 text-emerald-700";
  return "bg-slate-100 text-slate-700";
}

function resolveDurationMinutes(item) {
  return resolvePlannedMinutes(item);
}

function resolveAssignedLabel(id, monteurs) {
  if (!id) return "Niet toegewezen";
  const found = monteurs.find((item) => String(item?._id || "") === String(id));
  return found?.naam || found?.email || "Monteur";
}

function getStatusPresentation({
  statusKey,
  plannedDate,
  plannedEndDate,
  primaryAssignedUserId,
}) {
  if (statusKey === "gereed_voor_planning") {
    if (!plannedDate || !primaryAssignedUserId) {
      return {
        label: "Ongepland",
        dotClass: "bg-amber-500",
        badgeClass: "bg-amber-50 text-amber-800 border border-amber-200",
      };
    }

    return {
      label: "Voorbereiding",
      dotClass: "bg-amber-400",
      badgeClass: "bg-amber-50 text-amber-800 border border-amber-200",
    };
  }

  if (statusKey === "gepland") {
    if (plannedDate && plannedEndDate && plannedEndDate !== plannedDate) {
      return {
        label: "Doorlopend",
        dotClass: "bg-violet-500",
        badgeClass: "bg-violet-50 text-violet-800 border border-violet-200",
      };
    }

    return {
      label: "Uitgegeven",
      dotClass: "bg-sky-500",
      badgeClass: "bg-sky-50 text-sky-800 border border-sky-200",
    };
  }

  const map = {
    concept: {
      label: "Nieuw",
      dotClass: "bg-slate-400",
      badgeClass: "bg-slate-100 text-slate-700 border border-slate-200",
    },
    in_uitvoering: {
      label: "In uitvoering",
      dotClass: "bg-orange-500",
      badgeClass: "bg-orange-50 text-orange-800 border border-orange-200",
    },
    tijdelijk_stilgelegd: {
      label: "Controle",
      dotClass: "bg-violet-500",
      badgeClass: "bg-violet-50 text-violet-800 border border-violet-200",
    },
    afgerond: {
      label: "Gereed",
      dotClass: "bg-emerald-500",
      badgeClass: "bg-emerald-50 text-emerald-800 border border-emerald-200",
    },
    gesloten: {
      label: "Gefactureerd",
      dotClass: "bg-slate-900",
      badgeClass: "bg-slate-100 text-slate-700 border border-slate-200",
    },
  };

  return (
    map[statusKey] || {
      label: "Nieuw",
      dotClass: "bg-slate-400",
      badgeClass: "bg-slate-100 text-slate-700 border border-slate-200",
    }
  );
}

function getTimelineColor(workTypeLabel) {
  if (workTypeLabel === "Storing") return "bg-rose-500";
  if (workTypeLabel === "Project") return "bg-sky-600";
  return "bg-emerald-600";
}

function normalizeDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return toISODate(date);
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, "-");
}
